import { http } from '@/lib/axios';
import { toApiError } from '@/lib/unwrap';

/**
 * One operator-blocked visit-date range. `dateFrom`/`dateTo` are civil days
 * (Asia/Jakarta) as `YYYY-MM-DD`, inclusive on both ends.
 */
export interface BlockedDate {
  dateFrom: string;
  dateTo: string;
  reason: string;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function isValidBlock(b: unknown): b is BlockedDate {
  if (!b || typeof b !== 'object') return false;
  const { dateFrom, dateTo } = b as Partial<BlockedDate>;
  return (
    typeof dateFrom === 'string' &&
    typeof dateTo === 'string' &&
    ISO_DAY.test(dateFrom) &&
    ISO_DAY.test(dateTo)
  );
}

/**
 * Defensive extractor — the feed answers with the standard envelope
 * (`{ status, data: { blockedDates } }`) but tolerates a bare
 * `{ blockedDates }` or a plain array. Malformed entries are dropped.
 */
export function extractBlockedDates(payload: unknown): BlockedDate[] {
  if (!payload) return [];
  let raw: unknown = payload;
  if (!Array.isArray(raw)) {
    const p = raw as { blockedDates?: unknown; data?: { blockedDates?: unknown } | unknown[] };
    if (Array.isArray(p.blockedDates)) raw = p.blockedDates;
    else if (Array.isArray(p.data)) raw = p.data;
    else if (p.data && Array.isArray((p.data as { blockedDates?: unknown }).blockedDates)) {
      raw = (p.data as { blockedDates: unknown[] }).blockedDates;
    } else raw = [];
  }
  return (raw as unknown[]).filter(isValidBlock).map((b) => ({
    dateFrom: b.dateFrom,
    dateTo: b.dateTo,
    reason: typeof b.reason === 'string' ? b.reason : '',
  }));
}

/**
 * The block covering a civil day (`YYYY-MM-DD`), or null. Inclusive on both
 * ends. Pure string comparison — `YYYY-MM-DD` sorts chronologically — so the
 * answer never depends on the terminal's own time zone.
 */
export function findBlockForDate(
  dateISO: string | null | undefined,
  blocks: readonly BlockedDate[] | null | undefined,
): BlockedDate | null {
  if (typeof dateISO !== 'string' || !ISO_DAY.test(dateISO)) return null;
  if (!blocks || blocks.length === 0) return null;
  for (const b of blocks) {
    if (!isValidBlock(b)) continue;
    if (dateISO >= b.dateFrom && dateISO <= b.dateTo) return b;
  }
  return null;
}

/**
 * GET /capacity/blocked-dates/public — operator-blocked visit dates (only
 * today-or-future, customer-visible blocks). The authoritative guard is the
 * sale endpoint's 409 VISIT_DATE_BLOCKED; this feed drives the UI lock.
 */
export async function getBlockedDates(): Promise<BlockedDate[]> {
  try {
    const res = await http.get('/capacity/blocked-dates/public');
    return extractBlockedDates(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
